import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Input, Option, Select, Spinner } from '@material-tailwind/react';
import { ArrowLeftIcon, CalendarIcon, MapPinIcon, PencilIcon, SendIcon, Trash2Icon, PaperclipIcon, XIcon } from 'lucide-react';
import { URLS } from '../../router-x/routes';
import { usePermissions } from '../../custom-hooks/use-permissions';
import { useConfirm } from '../../custom-hooks/confirm-dialog';
import { useGetUsers } from '../../custom-hooks/use-users';
import {
    useGetWorkOrderById, useDeleteWorkOrder, useUpdateWorkOrder, useUpdateWorkOrderStatus, useUpdateWorkOrderCost,
    useGetWorkOrderInteractions, useAddWorkOrderInteraction,
} from '../../custom-hooks/use-maintenance';
import { formatNumberWithCommasPakistani } from '../../utils/functions';
import { STATIC_USER_ROLES, MAINTENANCE_STATUSES } from '../../utils/enums';
import WorkOrderFormModal from './components/WorkOrderFormModal';
import TabBar from './components/TabBar';
import CompleteWorkOrderModal from './components/CompleteWorkOrderModal';
import EstimateGateModal from './components/EstimateGateModal';
import { CATEGORY_META, PRIORITY_META, STATUS_META, STATUS_COLUMNS, getSingleLocationLabel, getLocations, getInitials, isOverdue } from './utils';

const ACTIVITY_FILTER_TABS = [
    { key: 'comments', label: 'Comments' },
    { key: 'activity', label: 'Activity' },
    { key: 'both', label: 'Both' },
];

// attachment.url is a base64 data URI stored directly on the document (same pattern
// as the Leads module) — no async resolution needed, unlike a Storage-backed path.
const AttachmentChip = ({ attachment }) => (
    <a
        href={attachment.url}
        download={attachment.name}
        target="_blank"
        rel="noreferrer"
        className="flex cursor-pointer items-center gap-1.5 rounded-full bg-inv-cream-100 px-3 py-1 text-xs text-inv-ink hover:bg-inv-cream-200"
    >
        <PaperclipIcon size={12} />
        {attachment.name}
    </a>
);

const MaintenanceWorkOrderDetail = () => {
    const { workOrderId } = useParams();
    const navigate = useNavigate();
    const { can } = usePermissions();
    const { confirm, ConfirmationDialog } = useConfirm();

    const [editOpen, setEditOpen] = useState(false);
    const [comment, setComment] = useState('');
    const [activityFilter, setActivityFilter] = useState('comments');
    const [costInput, setCostInput] = useState('');
    const [editingCost, setEditingCost] = useState(false);
    const [showCompleteGate, setShowCompleteGate] = useState(false);
    const [pendingEstimateStatus, setPendingEstimateStatus] = useState(null);

    const { data: workOrder, isLoading } = useGetWorkOrderById(workOrderId);
    const { data: interactions = [] } = useGetWorkOrderInteractions(workOrderId);
    const { data: staffData } = useGetUsers({
        role: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.EMPLOYEE, STATIC_USER_ROLES.STAFF],
        _page: 1,
        _limit: 1000,
    });
    const staff = staffData?.users || [];

    const deleteMutation = useDeleteWorkOrder();
    const updateMutation = useUpdateWorkOrder();
    const statusMutation = useUpdateWorkOrderStatus();
    const costMutation = useUpdateWorkOrderCost();
    const addCommentMutation = useAddWorkOrderInteraction();

    useEffect(() => {
        setCostInput(workOrder?.actualCost ?? '');
    }, [workOrder?._id, workOrder?.actualCost]);

    if (isLoading) {
        return <div className="flex min-h-[50vh] items-center justify-center"><Spinner className="h-8 w-8 text-inv-brand" /></div>;
    }
    if (!workOrder) {
        return <div className="py-16 text-center text-[#8C8175]">Work order not found.</div>;
    }

    const canViewCosts = can('FX32', 'view_costs');
    const canEdit = can('FX32', 'edit');
    const canDelete = can('FX32', 'delete');
    const canLogCost = can('FX32', 'log_cost') || canEdit;
    const canComment = can('FX32', 'comment');
    const canChangeStatus = can('FX32', 'update_status') || canEdit;
    const canAssign = canEdit;
    const overdue = isOverdue(workOrder);
    const filteredInteractions = activityFilter === 'both'
        ? interactions
        : interactions.filter((entry) => (activityFilter === 'comments' ? entry.type === 'comment' : entry.type !== 'comment'));

    const handleDelete = async () => {
        const ok = await confirm({
            title: 'Delete Work Order',
            message: `Delete "${workOrder.title}"? This cannot be undone.`,
            confirmText: 'Delete',
            confirmBtnColor: 'red',
        });
        if (!ok) return;
        await deleteMutation.mutateAsync(workOrder._id);
        navigate(URLS.ADMIN.MAINTENANCE);
    };

    const handleStatusChange = (status) => {
        if (status === workOrder.status) return;
        // Leaving To Do needs an estimate. Checked before the Completed gate because a
        // work order with neither would otherwise be asked for the actual cost first,
        // which is the wrong question to lead with.
        if (workOrder.status === MAINTENANCE_STATUSES.TODO && workOrder.estimate == null) {
            setPendingEstimateStatus(status);
            return;
        }
        if (status === MAINTENANCE_STATUSES.COMPLETED && workOrder.actualCost == null) {
            setShowCompleteGate(true);
            return;
        }
        statusMutation.mutate({ workOrderId: workOrder._id, data: { status } });
    };

    const handleConfirmEstimate = (estimate) => {
        // Estimate and status travel together so the gate is one atomic request.
        statusMutation.mutate({
            workOrderId: workOrder._id,
            data: { status: pendingEstimateStatus, estimate },
        });
        setPendingEstimateStatus(null);
    };

    const handleConfirmComplete = (actualCost) => {
        statusMutation.mutate({ workOrderId: workOrder._id, data: { status: MAINTENANCE_STATUSES.COMPLETED, actualCost } });
        setShowCompleteGate(false);
    };

    const handleAssigneeChange = (assignee) => {
        updateMutation.mutate({ workOrderId: workOrder._id, data: { assignee: assignee || null } });
    };

    const handleSaveCost = async () => {
        if (costInput === '') return;
        await costMutation.mutateAsync({ workOrderId: workOrder._id, data: { actualCost: Number(costInput) } });
        setEditingCost(false);
    };

    const handleSendComment = async () => {
        if (!comment.trim()) return;
        await addCommentMutation.mutateAsync({ workOrderId: workOrder._id, data: { message: comment.trim() } });
        setComment('');
    };

    return (
        <div className="min-h-screen bg-inv-cream px-4 py-6 md:px-8 md:py-8">
            <Link to={URLS.ADMIN.MAINTENANCE} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#6B675F] hover:text-inv-brand">
                <ArrowLeftIcon size={15} /> Back to Maintenance
            </Link>

            <div className="mb-5 flex flex-col justify-between gap-4 rounded-2xl border border-inv-cream-200 bg-white p-5 md:flex-row md:items-start">
                <div>
                    <div className="flex flex-wrap items-center gap-2">
                        <h1 className="text-xl font-extrabold text-inv-ink">{workOrder.title}</h1>
                        {overdue && <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-600">Overdue</span>}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${PRIORITY_META[workOrder.priority]?.badgeClass || 'bg-gray-50 text-gray-600'}`}>
                            {PRIORITY_META[workOrder.priority]?.label}
                        </span>
                        <span className="rounded-md bg-gray-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-gray-600">
                            {CATEGORY_META[workOrder.category]?.label}
                        </span>
                    </div>
                    <div className="mt-3 flex flex-col gap-1.5 text-sm text-[#6B675F]">
                        {/* The detail page has room to list every location in full, so it
                            does — the "+N more" summary is for the cards, where a work
                            order covering a whole branch would otherwise overflow. */}
                        <div className="flex items-start gap-2">
                            <MapPinIcon size={15} className="mt-0.5 shrink-0 text-[#8C8175]" />
                            <div className="flex min-w-0 flex-col gap-0.5">
                                {getLocations(workOrder).length === 0
                                    ? '—'
                                    : getLocations(workOrder).map((loc, index) => (
                                        <span key={loc._id || index} className="break-words">
                                            {getSingleLocationLabel(loc)}
                                        </span>
                                    ))}
                            </div>
                        </div>
                        <div className={`flex items-center gap-2 ${overdue ? 'font-semibold text-red-600' : ''}`}>
                            <CalendarIcon size={15} className="shrink-0 text-[#8C8175]" />
                            Due {new Date(workOrder.dueDate).toLocaleDateString()}
                        </div>
                    </div>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                    {canEdit && (
                        <button onClick={() => setEditOpen(true)} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-inv-cream-200 text-[#6B675F] hover:bg-inv-cream-100">
                            <PencilIcon size={16} />
                        </button>
                    )}
                    {canDelete && (
                        <button onClick={handleDelete} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-inv-cream-200 text-[#6B675F] hover:bg-red-50 hover:text-red-600">
                            <Trash2Icon size={16} />
                        </button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
                <div className="flex flex-col gap-5 lg:col-span-2">
                    <div className="rounded-2xl border border-inv-cream-200 bg-white p-5">
                        <h3 className="mb-3 font-bold text-inv-ink">Details</h3>
                        {workOrder.description ? (
                            <p className="whitespace-pre-wrap text-sm text-[#6B675F]">{workOrder.description}</p>
                        ) : (
                            <p className="text-sm text-[#8C8175]">No description provided.</p>
                        )}

                        {workOrder.attachments?.length > 0 && (
                            <div className="mt-4 flex flex-wrap gap-2">
                                {workOrder.attachments.map((att, index) => <AttachmentChip key={`${att.name}-${index}`} attachment={att} />)}
                            </div>
                        )}
                    </div>

                    <div className="rounded-2xl border border-inv-cream-200 bg-white p-5">
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                            <h3 className="font-bold text-inv-ink">Comments &amp; Activity</h3>
                            <TabBar tabs={ACTIVITY_FILTER_TABS} activeTab={activityFilter} onChange={setActivityFilter} size="sm" />
                        </div>
                        <div className="flex max-h-96 flex-col gap-3 overflow-y-auto custom-scrollbar pr-1">
                            {filteredInteractions.map((entry) => (
                                entry.type === 'comment' ? (
                                    <div key={entry._id} className="flex items-start gap-2">
                                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-inv-cream-100 text-[10px] font-bold text-inv-ink">
                                            {getInitials(entry.sender?.name)}
                                        </span>
                                        <div className="flex-1 rounded-xl bg-inv-cream-100/60 px-3 py-2">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="text-xs font-semibold text-inv-ink">{entry.sender?.name || 'Unknown'}</span>
                                                <span className="text-[10px] text-[#8C8175]">{new Date(entry.createdDate).toLocaleString()}</span>
                                            </div>
                                            <p className="mt-0.5 whitespace-pre-wrap text-sm text-[#4A453E]">{entry.message}</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div key={entry._id} className="pl-9 text-xs italic text-[#8C8175]">
                                        <span className="font-semibold not-italic text-[#6B675F]">{entry.sender?.name || 'Someone'}</span> — {entry.message} · {new Date(entry.createdDate).toLocaleString()}
                                    </div>
                                )
                            ))}
                            {filteredInteractions.length === 0 && (
                                <p className="text-xs text-[#8C8175]">{activityFilter === 'comments' ? 'No comments yet.' : 'No activity yet.'}</p>
                            )}
                        </div>

                        {canComment && (
                            <div className="mt-3 flex items-center gap-2">
                                <Input
                                    label="Add a comment"
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSendComment(); }}
                                />
                                <Button size="sm" className="flex items-center gap-1.5 rounded-full bg-inv-brand px-3 py-2.5 shadow-none hover:bg-inv-brand-dark" onClick={handleSendComment} loading={addCommentMutation.isLoading}>
                                    <SendIcon size={14} />
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex flex-col gap-5">
                    <div className="rounded-2xl border border-inv-cream-200 bg-white p-5">
                        <h3 className="mb-3 font-bold text-inv-ink">Status &amp; Assignment</h3>
                        <div className="flex flex-col gap-3">
                            <Select label="Status" value={workOrder.status} disabled={!canChangeStatus} onChange={handleStatusChange} selected={() => STATUS_META[workOrder.status]?.label}>
                                {STATUS_COLUMNS.map((s) => <Option key={s} value={s}>{STATUS_META[s]?.label}</Option>)}
                            </Select>

                            <Select
                                label="Assignee"
                                value={workOrder.assignee?._id || ''}
                                disabled={!canAssign}
                                onChange={handleAssigneeChange}
                                menuProps={{ className: 'max-h-64 overflow-y-auto' }}
                                selected={() => workOrder.assignee?.name || 'Unassigned'}
                            >
                                <Option value="">Unassigned</Option>
                                {staff.map((u) => <Option key={u._id} value={u._id}>{u.name || u.email}</Option>)}
                            </Select>

                            {!canAssign && (
                                <div className="flex items-center gap-2 text-sm text-inv-ink">
                                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-inv-brand-light text-[11px] font-bold text-inv-brand">
                                        {getInitials(workOrder.assignee?.name)}
                                    </span>
                                    {workOrder.assignee?.name || 'Unassigned'}
                                </div>
                            )}
                        </div>
                    </div>

                    {canViewCosts && (
                        <div className="rounded-2xl border border-inv-cream-200 bg-white p-5">
                            <h3 className="mb-3 font-bold text-inv-ink">Cost</h3>
                            <div className="flex flex-col gap-3">
                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#8C8175]">Estimate</p>
                                    <p className="text-lg font-extrabold text-inv-ink">
                                        {workOrder.estimate != null
                                            ? `${formatNumberWithCommasPakistani(workOrder.estimate)} PKR`
                                            : 'Not estimated yet'}
                                    </p>
                                </div>
                                <div>
                                    <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[#8C8175]">Actual Cost / Payment</p>
                                    {editingCost ? (
                                        <div className="flex items-center gap-2">
                                            <Input type="number" value={costInput} onChange={(e) => setCostInput(e.target.value)} autoFocus />
                                            <Button size="sm" className="rounded-full bg-inv-brand px-3 py-2.5 shadow-none hover:bg-inv-brand-dark" onClick={handleSaveCost} loading={costMutation.isLoading}>
                                                Save
                                            </Button>
                                            <button
                                                onClick={() => { setEditingCost(false); setCostInput(workOrder.actualCost ?? ''); }}
                                                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[#8C8175] hover:bg-inv-cream-100"
                                                aria-label="Cancel"
                                            >
                                                <XIcon size={16} />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <p className="text-lg font-extrabold text-inv-ink">{workOrder.actualCost != null ? `${formatNumberWithCommasPakistani(workOrder.actualCost)} PKR` : '—'}</p>
                                            {canLogCost && (
                                                <button
                                                    onClick={() => setEditingCost(true)}
                                                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[#8C8175] hover:bg-inv-cream-100 hover:text-inv-ink"
                                                    aria-label="Edit actual cost"
                                                >
                                                    <PencilIcon size={13} />
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <WorkOrderFormModal isOpen={editOpen} onClose={() => setEditOpen(false)} workOrder={workOrder} />
            <EstimateGateModal
                isOpen={Boolean(pendingEstimateStatus)}
                onClose={() => setPendingEstimateStatus(null)}
                workOrder={workOrder}
                targetStatus={pendingEstimateStatus}
                onConfirm={handleConfirmEstimate}
                isSubmitting={statusMutation.isLoading}
            />

            <CompleteWorkOrderModal
                isOpen={showCompleteGate}
                onClose={() => setShowCompleteGate(false)}
                workOrder={workOrder}
                onConfirm={handleConfirmComplete}
                isSubmitting={statusMutation.isLoading}
            />
            <ConfirmationDialog />
        </div>
    );
};

export default MaintenanceWorkOrderDetail;
